import { useEffect } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import { LinearProgress } from '../../../_components/ProgressBar.js';
import type { SurveyTheme } from '../types.js';
import { BrandHeader } from './BrandHeader.js';

interface SurveyShellProps {
  palette: Palette;
  onDismiss: () => void;
  progressRatio?: number;
  theme?: SurveyTheme;
  hideBranding?: boolean;
  children: ReactNode;
}

export function SurveyShell({ palette, onDismiss, progressRatio, theme, hideBranding, children }: SurveyShellProps): JSX.Element {
  // Escape and the X button are the only ways out. Outside-click is not
  // wired up on purpose so brushing the backdrop can't discard a partial
  // answer set.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div role="dialog" aria-modal="true" className="chirp-survey-backdrop" style={backdropStyle(palette)}>
      <div className="chirp-survey-container" style={containerStyle(palette)}>
        {theme?.customCss && <CustomCssLayer css={theme.customCss} />}
        {theme?.backgroundImage && <BackgroundLayer theme={theme} />}
        <button
          type="button"
          aria-label="Close survey"
          onClick={onDismiss}
          className="chirp-survey-close"
          style={closeButtonStyle(palette)}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = hexWithAlpha(palette.text, 0.08);
            event.currentTarget.style.color = palette.text;
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent';
            event.currentTarget.style.color = palette.textMuted;
          }}
        >
          ×
        </button>
        {progressRatio !== undefined && (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <LinearProgress palette={palette} ratio={progressRatio} />
          </div>
        )}
        <div style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
          <BrandHeader palette={palette} theme={theme} hideBranding={hideBranding} />
        </div>
        <div style={contentStyle()}>{children}</div>
      </div>
    </div>
  );
}

// Injects author-supplied CSS into the survey container. Survives across
// re-renders because <style> is part of React's tree — when the survey
// unmounts (close, navigate away) the styles tear down with it. Trusted-
// content surface: only survey authors can write this and the iframe
// isolates it from the host page.
function CustomCssLayer({ css }: { css: string }): JSX.Element {
  // dangerouslySetInnerHTML is intentional — React would otherwise escape
  // CSS special chars. The value comes from the survey author and is
  // confined to the iframe, so the XSS surface is the same as their
  // existing access to the survey config.
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

function BackgroundLayer({ theme }: { theme: SurveyTheme }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${theme.backgroundImage})`,
        backgroundSize: theme.backgroundImageFit ?? 'cover',
        backgroundPosition: theme.backgroundImagePosition ?? 'center',
        opacity: normalizeOpacity(theme.backgroundImageOpacity),
        pointerEvents: 'none',
      }}
    />
  );
}

// Dashboard stores opacity as 0–100, model/SDK expect 0–1; normalize.
function normalizeOpacity(raw: number | undefined): number {
  if (typeof raw !== 'number') return 1;
  return raw > 1 ? raw / 100 : raw;
}

function backdropStyle(palette: Palette): CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: palette.font,
  };
}

function closeButtonStyle(palette: Palette): CSSProperties {
  return {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 2,
    width: 32,
    height: 32,
    borderRadius: 999,
    border: 'none',
    background: 'transparent',
    color: palette.textMuted,
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 120ms ease, color 120ms ease',
  };
}

function containerStyle(palette: Palette): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    maxWidth: 672,
    background: palette.background,
    borderRadius: palette.radiusLg,
    border: `1px solid ${palette.border}`,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    color: palette.text,
    maxHeight: '75vh',
    display: 'flex',
    flexDirection: 'column',
  };
}

function contentStyle(): CSSProperties {
  return {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    // Fallback scroll container for WelcomePage / ThankYouView. PagedSurvey
    // and ConversationalSurvey each set up their own scroll area inside
    // this so they can pin a footer or chevron rail to the bottom — those
    // children effectively act as a single scrolled child here.
    overflow: 'auto',
  };
}
